import {
    Customer,
    EN16931_TYPE_CODE,
    Invoice,
    InvoiceCompanySnapshot,
    InvoiceConfig,
    InvoiceLine,
    InvoiceLineKind,
    InvoiceTotals,
    TaxCategoryCode,
    TaxGroup,
} from '../../types';
import {
    effectiveRetentionPercent,
    performancePeriod,
    presentInvoiceLines,
    printedServicePeriod,
} from '../invoiceLogic';
import { Cents, formatCentsPlain, roundCents } from '../money';

/**
 * XRechnung 3.0 in der Syntax UBL 2.1 aus einem Beleg des Rechnungsmoduls.
 *
 * Warum von Hand und ohne Bibliothek: die verfuegbaren UBL-Generatoren bringen
 * ein eigenes Betragsmodell mit (Float oder Decimal-Objekte). Der gesamte
 * Beleg liegt hier aber als ganzzahliger Cent vor, und genau diese Zahl steht
 * bereits im archivierten PDF. Jede Umrechnung ueber einen Float waere eine
 * zweite Wahrheit — PDF und XML wuerden frueher oder spaeter um einzelne Cent
 * auseinanderlaufen. Deshalb werden die Dezimalstrings direkt aus den
 * Cent-Ganzzahlen gebildet, ohne Division.
 *
 * Bewusste Abgrenzung: dieses Modul PRUEFT nicht. Ob der Beleg die
 * Geschaeftsregeln erfuellt, entscheidet einzig en16931Rules.ts. Der Generator
 * bildet ab, was da ist, und laesst leere Felder weg — sonst wuerde ein
 * fehlender Wert als leeres Pflichtelement durch die Schema-Pruefung rutschen
 * und erst beim Empfaenger auffallen.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Konstanten
// ═══════════════════════════════════════════════════════════════════════════

/** BT-24. Kennzeichnet den Beleg als XRechnung 3.0 auf Basis der EN 16931. */
export const CUSTOMIZATION_ID =
    'urn:cen.eu:en16931:2017#compliant#urn:xoev-de:kosit:standard:xrechnung_3.0';

/** BT-23. Peppol BIS Billing 3.0 — der Transportweg, ueber den XRechnungen laufen. */
export const PROFILE_ID = 'urn:fdc:peppol.eu:2017:poacc:billing:01:1.0';

/** BT-5. Das Modul rechnet ausschliesslich in Euro-Cent, es gibt keine zweite Waehrung. */
export const DOCUMENT_CURRENCY_CODE = 'EUR';

/** Rueckfall fuer BT-40/BT-55, solange die Stammdaten kein Land fuehren. */
export const DEFAULT_COUNTRY_CODE = 'DE';

/** UNTDID 4461 Code 58 — SEPA-Ueberweisung. Der einzige real genutzte Weg. */
export const PAYMENT_MEANS_SEPA_CREDIT_TRANSFER = '58';

const UBL_NAMESPACES: Record<string, string> = {
    xmlns: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
    'xmlns:cac': 'urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2',
    'xmlns:cbc': 'urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2',
};

/**
 * Zeilenarten, die zu einer cac:InvoiceLine werden.
 *
 * Deckungsgleich mit den Arten, die in invoiceLogic zum Netto beitragen. Nur so
 * gilt BR-CO-10 (Summe der Positionsnetto == BT-106) ohne Nacharbeit.
 */
export const XML_LINE_KINDS: ReadonlySet<InvoiceLineKind> = new Set<InvoiceLineKind>([
    'SERVICE',
    'DISCOUNT',
    'SURCHARGE',
]);

/**
 * Zeilenarten, die im XML als Anmerkung an der FOLGENDEN Position landen.
 *
 * BR-21 verlangt fuer jede Rechnungsposition eine Menge und einen Preis.
 * Zwischenueberschriften und Erlaeuterungstexte haben beides nicht; als eigene
 * Position mit Menge 0 waeren sie zwar schema-gueltig, wuerden beim Empfaenger
 * aber als Nullposition gebucht. Als cbc:Note bleiben sie sichtbar und
 * bleiben aus der Buchhaltung heraus.
 */
export const NOTE_LINE_KINDS: ReadonlySet<InvoiceLineKind> = new Set<InvoiceLineKind>([
    'SECTION',
    'TEXT',
]);

export const isXmlInvoiceLine = (line: InvoiceLine): boolean => XML_LINE_KINDS.has(line.kind);

// ═══════════════════════════════════════════════════════════════════════════
// Escaping und Formatierung
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Maskiert die fuenf XML-Metazeichen und entfernt Steuerzeichen.
 *
 * Sicherheitsrelevant, nicht kosmetisch: Kundenname, Positionstitel und alle
 * Freitexte stammen aus Benutzereingaben. Ein ungefiltertes "<" oder "&" macht
 * das Dokument nicht nur unwohlgeformt — es erlaubt, fremde Elemente in den
 * Beleg zu schreiben, also z.B. einen zweiten Zahlungsempfaenger.
 *
 * Die Steuerzeichen fliegen raus, weil XML 1.0 sie im Textinhalt verbietet
 * (ausser Tab, LF und CR). Sie kommen real aus Copy-und-Paste aus Word und
 * wuerden das Dokument beim Empfaenger am Parser scheitern lassen.
 */
export const escapeXml = (value: string): string =>
    String(value ?? '')
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

/**
 * Hundertstel als Dezimalstring mit genau zwei Nachkommastellen.
 *
 * Kein value/100, kein toFixed: beide gehen ueber einen Float. Bei 1_000_000_05
 * Cent liefert die Division bereits einen Wert, dessen letzte Stelle nicht mehr
 * exakt darstellbar ist. Ganzzahlige Division und Rest sind exakt.
 */
const decimalFromHundredths = (value: number): string => {
    const safe = Number.isFinite(value) ? Math.trunc(value) : 0;
    const negative = safe < 0;
    const abs = Math.abs(safe);
    const whole = Math.floor(abs / 100);
    const fraction = abs % 100;
    return `${negative ? '-' : ''}${whole}.${String(fraction).padStart(2, '0')}`;
};

/** Cent -> "1234.56". Die einzige erlaubte Betragsdarstellung im XML. */
export const decimalFromCents = (cents: Cents): string => decimalFromHundredths(cents);

/** Basispunkte -> "19.00". Dieselbe Mechanik, weil 1900 Basispunkte 19,00 % sind. */
export const decimalFromTaxRateBp = (taxRateBp: number): string => decimalFromHundredths(taxRateBp);

/**
 * Menge mit drei Nachkommastellen (BT-129).
 *
 * Anders als Geld ist die Menge im Modell ein echter Float (bis zu drei
 * Nachkommastellen laut InvoiceLine). toFixed ist hier also zulaessig und die
 * kuerzeste korrekte Darstellung.
 */
export const formatQuantity = (quantity: number | undefined): string => {
    const value = Number.isFinite(quantity) ? (quantity as number) : 0;
    const text = value.toFixed(3);
    // toFixed macht aus -0.0001 die Zeichenkette "-0.000". Ein negatives Null
    // ist im UBL zwar erlaubt, wird aber von Pruefwerkzeugen angemeckert.
    return text === '-0.000' ? '0.000' : text;
};

/**
 * Einheit nach UN/ECE Recommendation 20 (BT-130).
 *
 * Das Bestands-LV fuehrt die Einheit als freien Text ("Stk", "Std", "pauschal").
 * Ohne Zuordnung muesste jede Position auf C62 ("Stueck") fallen, was bei
 * Stundenleistungen fachlich falsch ist. Unbekanntes faellt weiterhin auf C62,
 * weil ein erfundener Code die Schema-Pruefung sicher reissen wuerde.
 */
const UNIT_CODES: Record<string, string> = {
    stk: 'H87',
    st: 'H87',
    stueck: 'H87',
    'stück': 'H87',
    psch: 'LS',
    pausch: 'LS',
    pauschal: 'LS',
    h: 'HUR',
    std: 'HUR',
    stunde: 'HUR',
    stunden: 'HUR',
    tag: 'DAY',
    tage: 'DAY',
    woche: 'WEE',
    wochen: 'WEE',
    monat: 'MON',
    monate: 'MON',
    // Kurzformen der Monatspauschale — dieselbe Grosszuegigkeit wie
    // serviceUnits.isMonthlyUnit, damit eine als Pauschale erkannte Einheit
    // nicht in der E-Rechnung auf C62 ("Stueck") zurueckfaellt. Der Punkt
    // ('Mon.', 'mtl.') ist oben schon abgestreift.
    mon: 'MON',
    mtl: 'MON',
    monatlich: 'MON',
    m: 'MTR',
    lfm: 'MTR',
    km: 'KMT',
    m2: 'MTK',
    'm²': 'MTK',
    qm: 'MTK',
    m3: 'MTQ',
    'm³': 'MTQ',
    kg: 'KGM',
    t: 'TNE',
    l: 'LTR',
    ltr: 'LTR',
    '%': 'P1',
};

export const resolveUnitCode = (unit: string | undefined): string => {
    const key = String(unit ?? '')
        .trim()
        .toLowerCase()
        .replace(/\.$/, '');
    return UNIT_CODES[key] ?? 'C62';
};

// ═══════════════════════════════════════════════════════════════════════════
// Defensive Zugriffe auf noch nicht typisierte Felder
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Liest ein optionales Textfeld, das der Typ (noch) nicht kennt.
 *
 * Gebraucht fuer Leitweg-ID, Laendercode und Lieferanschrift: diese Felder sind
 * fachlich beschlossen, stehen aber noch nicht in src/types.ts. Der Zugriff
 * ueber Record<string, unknown> haelt das Modul lauffaehig, sobald die Felder
 * nachgezogen werden, ohne dass hier etwas geaendert werden muss — und ohne
 * dass eine Typaenderung erzwungen wird, an der parallel gearbeitet wird.
 */
export const readOptionalText = (source: unknown, key: string): string | undefined => {
    if (!source || typeof source !== 'object') return undefined;
    const value = (source as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return undefined;
};

export const readOptionalFlag = (source: unknown, key: string): boolean | undefined => {
    if (!source || typeof source !== 'object') return undefined;
    const value = (source as Record<string, unknown>)[key];
    return typeof value === 'boolean' ? value : undefined;
};

export const readOptionalRecord = (source: unknown, key: string): Record<string, unknown> | undefined => {
    if (!source || typeof source !== 'object') return undefined;
    const value = (source as Record<string, unknown>)[key];
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
};

// ═══════════════════════════════════════════════════════════════════════════
// Adressen und Beteiligte
// ═══════════════════════════════════════════════════════════════════════════

export interface PostalAddress {
    street?: string;
    postalZone?: string;
    cityName?: string;
    countryCode: string;
}

/**
 * Zerlegt eine einzeilige Anschrift in Strasse, PLZ und Ort.
 *
 * InvoiceCompanySnapshot fuehrt die Anschrift als einen einzigen Text, die
 * XRechnung verlangt sie aber getrennt (BR-DE-3, BR-DE-4). Ankerpunkt ist die
 * fuenfstellige Postleitzahl — das einzige Muster, das sich in einer freien
 * deutschen Anschrift zuverlaessig wiederfindet.
 *
 * Bewusst genau fuenf Stellen und nicht "vier bis fuenf": ein Postfach oder
 * eine vierstellige Hausnummer wuerde sonst als Postleitzahl gelesen und die
 * Strasse verschlucken. Auslaendische Anschriften trifft das nicht, weil das
 * Modul ausschliesslich in Deutschland abrechnet.
 */
export const parseAddressLine = (
    raw: string | undefined,
): { street?: string; postalZone?: string; cityName?: string } => {
    const text = String(raw ?? '').trim();
    if (!text) return {};

    const parts = text
        .split(/\r?\n|,/)
        .map((part) => part.trim())
        .filter(Boolean);

    const zipIndex = parts.findIndex((part) => /(^|\s)\d{5}(\s|$)/.test(part));
    if (zipIndex < 0) {
        return { street: parts.join(' ') || undefined };
    }

    const match = parts[zipIndex].match(/(^|\s)(\d{5})(\s+(.*))?$/);
    return {
        street: parts.slice(0, zipIndex).join(' ') || undefined,
        postalZone: match?.[2],
        cityName: match?.[4]?.trim() || undefined,
    };
};

/** BT-40 bzw. BT-55. Ohne gepflegtes Land gilt Deutschland — das Modul rechnet in DE ab. */
export const resolveCountryCode = (source: unknown): string =>
    (readOptionalText(source, 'countryCode') ??
        readOptionalText(source, 'country') ??
        DEFAULT_COUNTRY_CODE).toUpperCase();

export const resolveSellerAddress = (company: InvoiceCompanySnapshot | undefined): PostalAddress => {
    const parsed = parseAddressLine(company?.address);
    return {
        street: readOptionalText(company, 'street') ?? parsed.street,
        postalZone: readOptionalText(company, 'zip') ?? parsed.postalZone,
        cityName: readOptionalText(company, 'city') ?? parsed.cityName,
        countryCode: resolveCountryCode(company),
    };
};

export const resolveBuyerAddress = (customer: Customer | undefined): PostalAddress => {
    // Die Einzelfelder gewinnen; der freie Text ist nur der Rueckfall fuer
    // Altkunden, bei denen nie getrennt erfasst wurde.
    const parsed = parseAddressLine(readOptionalText(customer, 'address'));
    return {
        street: customer?.street?.trim() || parsed.street,
        postalZone: customer?.zip?.trim() || parsed.postalZone,
        cityName: customer?.city?.trim() || parsed.cityName,
        countryCode: resolveCountryCode(customer),
    };
};

export interface SellerContact {
    name?: string;
    phone?: string;
    email?: string;
}

/**
 * BG-6 Kontaktangaben des Verkaeufers.
 *
 * Der Geschaeftsfuehrer ist der Rueckfall fuer BT-41: die XRechnung verlangt
 * einen benannten Ansprechpartner, und ein leeres Element waere schlechter als
 * die Person, die ohnehin im Briefkopf steht.
 */
export const resolveSellerContact = (
    company: InvoiceCompanySnapshot | undefined,
    config?: InvoiceConfig,
): SellerContact => ({
    name:
        readOptionalText(company, 'contactName') ??
        readOptionalText(config, 'contactName') ??
        company?.managingDirectors?.find((entry) => !!entry?.trim())?.trim(),
    phone: company?.phone?.trim() || readOptionalText(config, 'phone'),
    email: company?.email?.trim() || readOptionalText(config, 'email'),
});

export interface BankAccountInfo {
    iban?: string;
    bic?: string;
    holder?: string;
    bankName?: string;
}

/**
 * BG-17 Ueberweisungsdaten.
 *
 * Reihenfolge der Quellen: was am Beleg steht, schlaegt den Firmen-Snapshot,
 * und der schlaegt die aktuelle Konfiguration. Der Snapshot ist beim Ausstellen
 * eingefroren — die Konfiguration darf einen alten Beleg nicht nachtraeglich
 * auf ein neues Konto umlenken.
 */
export const resolveBankAccount = (
    invoice: Invoice,
    company: InvoiceCompanySnapshot | undefined,
    config?: InvoiceConfig,
): BankAccountInfo => {
    const bank = invoice?.details?.bankAccount;
    return {
        iban: bank?.iban?.trim() || company?.iban?.trim() || config?.iban?.trim(),
        bic: bank?.bic?.trim() || company?.bic?.trim() || config?.bic?.trim(),
        // Kontoinhaber: erst der ausdrueckliche (Beleg, Snapshot, Konfig),
        // erst DANN der Firmenname — die Bank fuehrt oft eine andere
        // Schreibweise als der Briefkopf.
        holder:
            bank?.holder?.trim() ||
            company?.accountHolder?.trim() ||
            config?.accountHolder?.trim() ||
            company?.name?.trim(),
        bankName: bank?.bankName?.trim() || company?.bankName?.trim() || config?.bankName?.trim(),
    };
};

/**
 * BT-10 Kaeuferreferenz.
 *
 * Bei oeffentlichen Auftraggebern ist das die Leitweg-ID. Sie hat im Modell
 * noch kein eigenes Feld, deshalb wird sie defensiv gelesen: erst am Beleg,
 * dann am Kunden. Die Kundennummer ist der letzte Rueckfall, damit ein
 * gewerblicher Empfaenger ueberhaupt eine Referenz bekommt — fuer eine echte
 * Leitweg-ID reicht sie nicht, das prueft BR-DE-LEITWEG-1.
 */
export const resolveBuyerReference = (
    invoice: Invoice,
    customer: Customer | undefined,
): string | undefined =>
    invoice?.details?.buyerReference?.trim() ||
    readOptionalText(invoice?.details, 'leitwegId') ||
    readOptionalText(customer, 'leitwegId') ||
    readOptionalText(customer, 'buyerReference') ||
    customer?.customerNumber?.trim() ||
    undefined;

/** BT-81. Solange nichts anderes am Beleg steht, wird per SEPA ueberwiesen. */
export const resolvePaymentMeansCode = (invoice: Invoice): string =>
    readOptionalText(invoice?.details, 'paymentMeansCode') ?? PAYMENT_MEANS_SEPA_CREDIT_TRANSFER;

/**
 * Skonto im strukturierten Format der XRechnung.
 *
 * Die XRechnung wertet Skonto nicht aus einem Freitext aus, sondern erwartet es
 * maschinenlesbar in BT-20. Ohne dieses Muster liest der Empfaenger den
 * Zahlungsabzug nicht und mahnt die Differenz an.
 *
 * Gibt undefined zurueck, wenn die Werte unbrauchbar sind (NaN aus einem
 * Formularfeld, negative Tage). Lieber gar keine Angabe als eine falsche.
 */
export const formatSkontoTerms = (
    skonto: { percent?: number; days?: number } | undefined,
): string | undefined => {
    const percent = Number(skonto?.percent);
    const days = Number(skonto?.days);
    if (!Number.isFinite(percent) || percent <= 0) return undefined;
    if (!Number.isInteger(days) || days <= 0) return undefined;
    return `#SKONTO#TAGE=${days}#PROZENT=${percent.toFixed(2)}#`;
};

// ── Sicherheitseinbehalt in der E-Rechnung ───────────────────────────────
//
// EN 16931 kennt keinen Sicherheitseinbehalt. BR-CO-16 erzwingt
// BT-115 = BT-112 − BT-113 + BT-114, und der Einbehalt ist keine
// Entgeltminderung (docs/sicherheitseinbehalt-plan.md §2, §16.3). Im
// kumulativen Modell (computeRetention) heisst das:
//
//   BT-113 = paidPrior = prepaid − prior   (tatsaechlich vereinnahmt: die
//            Vorbelege FORDERTEN prepaid, gezahlt wurde davon nur der Teil
//            ohne die dort einbehaltenen Sicherheiten — die Zahl, die der
//            Kunde in seinen Ueberweisungen wiederfindet)
//   BT-115 = gross − BT-113 = payable + cumulative
//            (der Einbehalt ist FAELLIG und nur als Sicherheit einbehalten;
//            er steht deshalb im faelligen Betrag und als Text in BT-20/BT-22)
//
// Auf der Schlussrechnung mit Freigabe ist cumulative 0 und BT-115 exakt
// payableCents. Ohne Einbehalt ist prior 0, also paidPrior = prepaid und
// cumulative = 0 — beide Werte sind zeichengleich zu prepaidCents und
// payableCents, bestehende Belege aendern sich nicht.
//
// Fehlende Felder (Altbestand vor dem Feature, Belege des ersten Baus ohne
// paidPrior/cumulative) werden aus den vorhandenen hergeleitet, so dass
// gross − BT-113 = payable + cumulative auch dort algebraisch aufgeht:
// prior = 0, paidPrior = prepaid − prior, cumulative = prior + retention −
// release (denn delta = cumulative − prior = retention − release).

/** Auf den Vorbelegen einbehalten — 0, wenn der Beleg das Feld nicht traegt. */
const priorRetentionOf = (totals: InvoiceTotals | undefined): Cents =>
    totals?.priorRetentionCents ?? 0;

/** round(pct x gross) auf den Leistungsstand; Altbestand: aus Einbehalt und Freigabe hergeleitet. */
export const en16931CumulativeRetentionCents = (totals: InvoiceTotals | undefined): Cents =>
    totals?.cumulativeRetentionCents ??
    priorRetentionOf(totals) + (totals?.retentionCents ?? 0) - (totals?.retentionReleaseCents ?? 0);

/** BT-113 "Paid amount": die tatsaechlich erhaltenen Abschlagszahlungen (prepaid − prior). */
export const en16931PrepaidCents = (totals: InvoiceTotals | undefined): Cents =>
    totals?.paidPriorCents ?? (totals?.prepaidCents ?? 0) - priorRetentionOf(totals);

/** BT-115 "Amount due for payment": Brutto abzueglich BT-113 = Zahlbetrag + kumulierter Einbehalt. */
export const en16931PayableCents = (totals: InvoiceTotals | undefined): Cents =>
    (totals?.grossTotalCents ?? 0) - en16931PrepaidCents(totals);

/**
 * "82,36 EUR" — Betrag fuer Notizen im XML. Bewusst der ISO-Code statt des
 * Euro-Zeichens: BT-5 nennt die Waehrung als "EUR", und ein Freitext, der
 * dieselbe Waehrung anders schreibt, liest sich in einem Validator-Protokoll
 * wie eine zweite Waehrung.
 */
const formatEurForNote = (cents: Cents): string => `${formatCentsPlain(cents)} EUR`;

/** "5" oder "2,5" — Prozentsatz ohne Nachkomma-Nullen, wie im PDF (invoiceLogic.formatPercent). */
const formatPercentForNote = (percent: number): string =>
    percent.toLocaleString('de-DE', { maximumFractionDigits: 2 });

/**
 * Der Einbehalt-Satz fuer BT-20 und BT-22.
 *
 * Der Empfaenger liest im XML einen faelligen Betrag MIT dem kumulierten
 * Einbehalt (BT-115) und im PDF einen Zahlbetrag OHNE ihn. Dieser Satz
 * nennt Grundlage, Gesamteinbehalt, den Anteil dieses Belegs, das bereits
 * frueher Einbehaltene und den Zahlbetrag — sonst mahnt die
 * Kreditorenbuchhaltung die Differenz an oder zahlt den Einbehalt
 * versehentlich aus. Die Freigabe auf der Schlussrechnung bekommt denselben
 * Hinweis in umgekehrter Richtung: dort ist der Zahlbetrag HOEHER als
 * Brutto minus Abschlaege.
 *
 * Abschlag:        "Sicherheitseinbehalt 5 % auf 51.726,33 EUR = 2.586,32 EUR,
 *                  davon 219,76 EUR mit dieser Rechnung, 2.366,56 EUR bereits
 *                  mit frueheren Rechnungen. Zahlbetrag 4.175,37 EUR. Freigabe
 *                  mit der Schlussrechnung."
 * Nachholfall:     ohne den "bereits"-Teil (prior 0).
 * Schlussrechnung: "Sicherheitseinbehalte von 2.586,32 EUR werden mit dieser
 *                  Schlussrechnung freigegeben und sind im Zahlbetrag enthalten."
 *
 * Der Prozentsatz ist der effektive Satz des Belegs
 * (effectiveRetentionPercent); fehlt er trotz Betrag (Altbestand,
 * Handeingabe), steht nur der Betrag — ein erfundener Satz waere schlimmer
 * als keiner.
 */
export const buildRetentionNote = (invoice: Invoice): string | undefined => {
    const totals = invoice?.totals;
    const cumulativeCents = en16931CumulativeRetentionCents(totals);
    const priorCents = priorRetentionOf(totals);
    const retentionCents = totals?.retentionCents ?? 0;
    const releaseCents = totals?.retentionReleaseCents ?? 0;
    const isFinal = invoice?.documentType === 'FINAL_INVOICE';
    const sentences: string[] = [];

    // Solange auf dem Leistungsstand etwas einbehalten ist, weicht BT-115 vom
    // Zahlbetrag ab — auch dann, wenn dieser Beleg selbst nichts Neues
    // einbehaelt (Leistungsstand unveraendert, prior deckt cumulative).
    if (cumulativeCents > 0) {
        const percent = effectiveRetentionPercent(invoice?.documentType, invoice?.details);
        const percentText = percent > 0 ? `${formatPercentForNote(percent)} % ` : '';
        const parts = [
            `Sicherheitseinbehalt ${percentText}auf ${formatEurForNote(totals?.grossTotalCents ?? 0)} = ${formatEurForNote(cumulativeCents)}`,
        ];
        if (retentionCents > 0) {
            parts.push(`davon ${formatEurForNote(retentionCents)} mit dieser Rechnung`);
        }
        if (priorCents > 0) {
            parts.push(
                retentionCents > 0
                    ? `${formatEurForNote(priorCents)} bereits mit früheren Rechnungen`
                    : 'bereits mit früheren Rechnungen einbehalten',
            );
        }
        sentences.push(`${parts.join(', ')}.`);
    }
    if (releaseCents > 0) {
        sentences.push(
            `Sicherheitseinbehalte von ${formatEurForNote(releaseCents)} werden mit dieser ` +
                `${isFinal ? 'Schlussrechnung' : 'Rechnung'} freigegeben und sind im Zahlbetrag enthalten.`,
        );
    }
    if (cumulativeCents > 0) {
        sentences.push(`Zahlbetrag ${formatEurForNote(totals?.payableCents ?? 0)}.`);
        // Eine Schlussrechnung, die den Satz stehen laesst (releaseRetention
        // false), kann nicht auf "die Schlussrechnung" verweisen — sie ist es.
        sentences.push(
            isFinal ? 'Der Sicherheitseinbehalt bleibt bestehen.' : 'Freigabe mit der Schlussrechnung.',
        );
    }

    return sentences.length ? sentences.join(' ') : undefined;
};

/**
 * BT-20 Zahlungsbedingungen: Freitext, Einbehalt-Hinweis und — falls
 * vorhanden — das Skonto-Muster, jeweils in eigener Zeile.
 *
 * Das Skonto-Muster bleibt die LETZTE Zeile und wird nie mit Text
 * vermischt: die XRechnung liest es maschinell, und der bisherige Aufbau
 * "Freitext, dann #SKONTO#…#" ist der, den bestehende Empfaenger kennen.
 * Der Einbehalt-Satz gehoert deshalb zum Freitext davor.
 */
export const buildPaymentTermsNote = (invoice: Invoice, config?: InvoiceConfig): string | undefined => {
    const text = invoice?.details?.paymentTerms?.trim() || config?.defaultPaymentTerms?.trim() || '';
    const retention = buildRetentionNote(invoice);
    const skonto = formatSkontoTerms(invoice?.details?.skonto);
    const parts = [text, retention, skonto].filter((part): part is string => !!part);
    return parts.length ? parts.join('\n') : undefined;
};

/** Steuerkategorie einer Position (BT-151). Ohne eigene Angabe folgt sie dem Satz. */
export const defaultTaxCategoryCode = (taxRateBp: number): TaxCategoryCode =>
    taxRateBp > 0 ? 'S' : 'Z';

export const lineTaxCategory = (line: InvoiceLine): TaxCategoryCode =>
    line?.taxCategoryCode ?? defaultTaxCategoryCode(line?.taxRateBp ?? 0);

// ═══════════════════════════════════════════════════════════════════════════
// XML-Baum
// ═══════════════════════════════════════════════════════════════════════════

interface XmlNode {
    name: string;
    attrs?: Record<string, string>;
    text?: string;
    children?: XmlNode[];
}

type XmlChild = XmlNode | undefined | false | null;

/**
 * Blattelement. Gibt undefined zurueck, wenn kein Inhalt da ist.
 *
 * Weglassen statt leer schreiben: ein <cbc:CityName/> ohne Inhalt erfuellt das
 * Schema formal, bricht aber die Geschaeftsregel BR-DE-3 erst beim Empfaenger.
 * Fehlt das Element, meldet schon die eigene Pruefung den Mangel.
 */
const leaf = (name: string, value: unknown, attrs?: Record<string, string>): XmlNode | undefined => {
    const text = value === undefined || value === null ? '' : String(value).trim();
    if (!text) return undefined;
    return { name, attrs, text };
};

/** Gruppenelement. Faellt weg, sobald kein einziges Kind uebrig bleibt. */
const group = (name: string, children: XmlChild[], attrs?: Record<string, string>): XmlNode | undefined => {
    const kept = children.filter((child): child is XmlNode => !!child);
    if (kept.length === 0) return undefined;
    return { name, attrs, children: kept };
};

const INDENT = '    ';

const renderNode = (node: XmlNode, depth: number): string[] => {
    const pad = INDENT.repeat(depth);
    const attrs = Object.entries(node.attrs ?? {})
        .map(([key, value]) => ` ${key}="${escapeXml(value)}"`)
        .join('');

    if (node.children?.length) {
        return [
            `${pad}<${node.name}${attrs}>`,
            ...node.children.flatMap((child) => renderNode(child, depth + 1)),
            `${pad}</${node.name}>`,
        ];
    }
    return [`${pad}<${node.name}${attrs}>${escapeXml(node.text ?? '')}</${node.name}>`];
};

const amount = (name: string, cents: Cents): XmlNode | undefined =>
    leaf(name, decimalFromCents(cents ?? 0), { currencyID: DOCUMENT_CURRENCY_CODE });

// ═══════════════════════════════════════════════════════════════════════════
// Bausteine
// ═══════════════════════════════════════════════════════════════════════════

const buildAddress = (address: PostalAddress): XmlNode | undefined =>
    group('cac:PostalAddress', [
        leaf('cbc:StreetName', address.street),
        leaf('cbc:CityName', address.cityName),
        leaf('cbc:PostalZone', address.postalZone),
        group('cac:Country', [leaf('cbc:IdentificationCode', address.countryCode)]),
    ]);

const buildSellerParty = (
    company: InvoiceCompanySnapshot | undefined,
    config?: InvoiceConfig,
): XmlNode | undefined => {
    const address = resolveSellerAddress(company);
    const contact = resolveSellerContact(company, config);
    const vatId = company?.vatId?.trim() || config?.vatId?.trim();
    const taxNumber = company?.taxNumber?.trim() || config?.taxNumber?.trim();
    const registerNumber = company?.registerNumber?.trim() || config?.registerNumber?.trim();

    return group('cac:AccountingSupplierParty', [
        group('cac:Party', [
            // BT-34: elektronische Adresse des Verkaeufers. schemeID EM = E-Mail,
            // der einzige Kanal, den das Modul tatsaechlich bedient.
            contact.email ? leaf('cbc:EndpointID', contact.email, { schemeID: 'EM' }) : undefined,
            group('cac:PartyName', [leaf('cbc:Name', company?.name)]),
            buildAddress(address),
            vatId
                ? group('cac:PartyTaxScheme', [
                      leaf('cbc:CompanyID', vatId),
                      group('cac:TaxScheme', [leaf('cbc:ID', 'VAT')]),
                  ])
                : undefined,
            // Steuernummer als zweite Registrierung mit Schema FC ("fiscal code").
            // BT-32 ist der Weg fuer Betriebe ohne USt-IdNr.
            taxNumber
                ? group('cac:PartyTaxScheme', [
                      leaf('cbc:CompanyID', taxNumber),
                      group('cac:TaxScheme', [leaf('cbc:ID', 'FC')]),
                  ])
                : undefined,
            group('cac:PartyLegalEntity', [
                leaf('cbc:RegistrationName', company?.name),
                leaf('cbc:CompanyID', registerNumber),
                // BT-33 ist die zusaetzliche Rechtsangabe des Verkaeufers und
                // wird beim Empfaenger als Fliesstext gelesen. Nur "Amtsgericht
                // Hamburg" ohne die Registernummer ist dort eine halbe Angabe —
                // sie gehoeren zusammen, auch wenn die Nummer zusaetzlich in
                // CompanyID steht.
                leaf(
                    'cbc:CompanyLegalForm',
                    [company?.registerCourt?.trim(), registerNumber]
                        .filter((part) => part && part.length > 0)
                        .join(', ') || undefined,
                ),
            ]),
            group('cac:Contact', [
                leaf('cbc:Name', contact.name),
                leaf('cbc:Telephone', contact.phone),
                leaf('cbc:ElectronicMail', contact.email),
            ]),
        ]),
    ]);
};

const buildBuyerParty = (customer: Customer | undefined): XmlNode | undefined => {
    const address = resolveBuyerAddress(customer);
    const vatId = readOptionalText(customer, 'vatId');

    return group('cac:AccountingCustomerParty', [
        group('cac:Party', [
            customer?.email?.trim()
                ? leaf('cbc:EndpointID', customer.email.trim(), { schemeID: 'EM' })
                : undefined,
            customer?.customerNumber?.trim()
                ? group('cac:PartyIdentification', [leaf('cbc:ID', customer.customerNumber.trim())])
                : undefined,
            group('cac:PartyName', [leaf('cbc:Name', customer?.name)]),
            buildAddress(address),
            vatId
                ? group('cac:PartyTaxScheme', [
                      leaf('cbc:CompanyID', vatId),
                      group('cac:TaxScheme', [leaf('cbc:ID', 'VAT')]),
                  ])
                : undefined,
            group('cac:PartyLegalEntity', [leaf('cbc:RegistrationName', customer?.name)]),
            group('cac:Contact', [
                leaf('cbc:Name', customer?.contacts?.find((c) => !!c?.name)?.name),
                leaf('cbc:Telephone', customer?.phone),
                leaf('cbc:ElectronicMail', customer?.email),
            ]),
        ]),
    ]);
};

const buildTaxCategory = (name: string, group_: TaxGroup): XmlNode | undefined =>
    group(name, [
        leaf('cbc:ID', group_.taxCategoryCode ?? defaultTaxCategoryCode(group_.taxRateBp ?? 0)),
        leaf('cbc:Percent', decimalFromTaxRateBp(group_.taxRateBp ?? 0)),
        // BR-S-10: bei Regelbesteuerung darf kein Befreiungsgrund stehen. Er
        // gehoert ausschliesslich an die befreiten Kategorien.
        (group_.taxCategoryCode ?? defaultTaxCategoryCode(group_.taxRateBp ?? 0)) !== 'S'
            ? leaf('cbc:TaxExemptionReason', group_.taxExemptionReason)
            : undefined,
        group('cac:TaxScheme', [leaf('cbc:ID', 'VAT')]),
    ]);

/**
 * BG-23 Steueraufschluesselung: genau eine cac:TaxSubtotal je Steuersatz.
 *
 * Die Gruppen kommen fertig aus invoiceLogic.groupTaxes und werden hier NICHT
 * neu berechnet — die Steuer wurde dort aus der Gruppensumme gebildet, nicht je
 * Position. Wer hier nachrechnete, wuerde bei vielen Kleinbetraegen eine andere
 * Summe erhalten als das PDF ausweist.
 */
const buildTaxTotal = (taxGroups: TaxGroup[], taxTotalCents: Cents): XmlNode | undefined =>
    group('cac:TaxTotal', [
        amount('cbc:TaxAmount', taxTotalCents),
        ...(taxGroups ?? []).map((entry) =>
            group('cac:TaxSubtotal', [
                amount('cbc:TaxableAmount', entry.taxableNetCents ?? 0),
                amount('cbc:TaxAmount', entry.taxCents ?? 0),
                buildTaxCategory('cac:TaxCategory', entry),
            ]),
        ),
    ]);

const buildInvoiceLine = (line: InvoiceLine, index: number, notes: string[]): XmlNode | undefined => {
    const quantity = line.quantity ?? 0;
    const unitPriceCents = line.unitPriceCents ?? 0;
    const netCents = line.netCents ?? 0;
    // Bruttobetrag der Position vor Nachlass. Die Differenz zum eingefrorenen
    // Netto ist der Nachlass — so bleibt BT-131 exakt der Wert aus dem PDF,
    // statt aus Menge mal Preis neu abgeleitet zu werden.
    const beforeDiscountCents = roundCents(quantity * unitPriceCents);
    const allowanceCents = beforeDiscountCents - netCents;
    const discountPercent = line.discountPercent ?? 0;

    return group('cac:InvoiceLine', [
        // BT-126 muss eindeutig sein. Die Positionsnummer des Belegs taugt dafuer
        // nicht: Nachlass- und Zuschlagszeilen tragen keine.
        leaf('cbc:ID', String(index + 1)),
        ...notes.map((note) => leaf('cbc:Note', note)),
        leaf('cbc:InvoicedQuantity', formatQuantity(quantity), {
            unitCode: resolveUnitCode(line.unit),
        }),
        amount('cbc:LineExtensionAmount', netCents),
        allowanceCents !== 0
            ? group('cac:AllowanceCharge', [
                  leaf('cbc:ChargeIndicator', 'false'),
                  leaf('cbc:AllowanceChargeReason', 'Nachlass'),
                  discountPercent > 0
                      ? leaf('cbc:MultiplierFactorNumeric', discountPercent.toFixed(2))
                      : undefined,
                  amount('cbc:Amount', allowanceCents),
                  amount('cbc:BaseAmount', beforeDiscountCents),
              ])
            : undefined,
        group('cac:Item', [
            leaf('cbc:Description', line.description),
            leaf('cbc:Name', line.title || line.posNumber || 'Position'),
            line.lvPosition?.trim()
                ? group('cac:SellersItemIdentification', [leaf('cbc:ID', line.lvPosition.trim())])
                : undefined,
            buildTaxCategory('cac:ClassifiedTaxCategory', {
                taxRateBp: line.taxRateBp ?? 0,
                taxCategoryCode: lineTaxCategory(line),
                taxableNetCents: netCents,
                taxCents: 0,
                taxExemptionReason: line.taxExemptionReason,
            }),
        ]),
        group('cac:Price', [amount('cbc:PriceAmount', unitPriceCents)]),
    ]);
};

/**
 * Verteilt Abschnitts- und Textzeilen auf die jeweils folgende Rechnungsposition.
 *
 * Was nach der letzten Position noch offen ist, hat keine Position mehr, an die
 * es gehaengt werden koennte, und wandert in die Dokumentanmerkung. Sonst
 * verschwaende ein Schlusstext lautlos aus dem XML, waehrend er im PDF steht.
 */
const buildLines = (
    lines: InvoiceLine[],
): { lineNodes: XmlNode[]; trailingNotes: string[] } => {
    const lineNodes: XmlNode[] = [];
    let pending: string[] = [];

    for (const line of lines ?? []) {
        if (NOTE_LINE_KINDS.has(line.kind)) {
            const note = [line.title, line.description]
                .map((part) => part?.trim())
                .filter((part): part is string => !!part)
                .join(' — ');
            if (note) pending.push(note);
            continue;
        }
        // Abschlagsabzuege sind keine Positionen, sondern BT-113. Als Zeile
        // wuerden sie die Bemessungsgrundlage mindern, obwohl die erbrachte
        // Gesamtleistung vollstaendig auszuweisen ist.
        if (!isXmlInvoiceLine(line)) continue;

        const node = buildInvoiceLine(line, lineNodes.length, pending);
        pending = [];
        if (node) lineNodes.push(node);
    }

    return { lineNodes, trailingNotes: pending };
};

// ═══════════════════════════════════════════════════════════════════════════
// Dokument
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Erzeugt das vollstaendige UBL-2.1-Dokument.
 *
 * Bewusste Einschraenkung: auch Gutschriften (Typcode 381) werden im
 * Invoice-Wurzelelement ausgeliefert. Peppol verlangt dafuer eine CreditNote,
 * die XRechnung akzeptiert beide Syntaxen. Ein zweites Wurzelelement mit
 * eigener Elementbenennung waere ein separater Generator — solange keine
 * Gutschrift an einen Peppol-Empfaenger geht, waere das Vorratshaltung.
 */
export const buildUblInvoice = (
    invoice: Invoice,
    customer: Customer | undefined,
    company: InvoiceCompanySnapshot | undefined,
    config?: InvoiceConfig,
): string => {
    const totals = invoice?.totals;
    // Dieselben Zeilen wie das PDF: gedeckte Positionen einer Monatspauschale
    // kommen zusammengefasst (presentInvoiceLines), die Summen bleiben gleich.
    const { lineNodes, trailingNotes } = buildLines(
        presentInvoiceLines({ lines: invoice?.lines ?? [], details: invoice?.details }),
    );
    const bank = resolveBankAccount(invoice, company, config);
    const paymentTerms = buildPaymentTermsNote(invoice, config);

    // Reihenfolge wie im PDF von oben nach unten. subjectNote steht dort im
    // Betreffblock direkt unter Ueberschrift, Projekt und Kostenstelle und
    // gehoert deshalb an die zweite Stelle. Ohne ihn stuende der Zusatztext im
    // PDF, fehlte aber im XML — und bei einer E-Rechnung ist das XML der
    // fuehrende Beleg, das PDF nur seine Sichtform.
    //
    // Der Einbehalt-Satz (BT-22) steht VOR dem Schlusstext, weil er im PDF
    // zum Summenblock gehoert, der ueber dem Schlusstext gedruckt wird. Er
    // steht zusaetzlich in BT-20: manche Empfaengersysteme zeigen nur die
    // Rechnungsnotizen an, andere nur die Zahlungsbedingungen — an beiden
    // Stellen erklaert er, warum PDF und XML verschiedene Zahlbetraege nennen.
    const documentNotes = [
        invoice?.details?.headerText,
        invoice?.details?.subjectNote,
        invoice?.details?.introText,
        invoice?.details?.reasonText,
        config?.smallBusiness ? config?.smallBusinessNote : undefined,
        buildRetentionNote(invoice),
        invoice?.details?.outroText,
        ...trailingNotes,
    ]
        .map((note) => note?.trim())
        .filter((note): note is string => !!note);

    // Genau die Zeitraeume, die das Sichtbild ausweist. XML und PDF stecken bei
    // ZUGFeRD in EINER Datei — nennen sie Verschiedenes, ist der Beleg
    // fehlerhaft, und der Empfaenger periodisiert nach dem falschen von beiden.
    //
    // BT-73/74 ist der ABRECHNUNGSZEITRAUM (worueber abgerechnet wird),
    // BT-72 der Leistungszeitpunkt — das ENDE des Leistungszeitraums, also der
    // letzten tatsaechlich abgerechneten Leistung.
    const period = printedServicePeriod(invoice);
    const deliveryDate = performancePeriod(invoice).to;
    const hasPeriod = !!period.from && !!period.to;

    const root: XmlNode = {
        name: 'Invoice',
        attrs: UBL_NAMESPACES,
        children: [
            leaf('cbc:CustomizationID', CUSTOMIZATION_ID),
            leaf('cbc:ProfileID', PROFILE_ID),
            leaf('cbc:ID', invoice?.invoiceNumber),
            leaf('cbc:IssueDate', invoice?.invoiceDate),
            // BT-9 nur, wenn tatsaechlich etwas zu zahlen ist. Auf einem Storno
            // oder einer Minderung faellt der Betrag an den Empfaenger zurueck;
            // ein Faelligkeitsdatum daneben liest sich als Zahlungsfrist und
            // widerspricht dem PDF, das dort eine Erstattung ausweist.
            leaf(
                'cbc:DueDate',
                (invoice?.totals?.payableCents ?? 0) < 0 ? undefined : invoice?.dueDate,
            ),
            leaf('cbc:InvoiceTypeCode', EN16931_TYPE_CODE[invoice?.documentType] ?? '380'),
            ...documentNotes.map((note) => leaf('cbc:Note', note)),
            leaf('cbc:DocumentCurrencyCode', DOCUMENT_CURRENCY_CODE),
            // BT-19 Kostenstelle des Kaeufers. Der Empfaenger bucht den Beleg
            // intern darauf; steht sie nur im PDF, muss sie beim automatisierten
            // Einlesen von Hand nachgetragen werden — genau das, was die
            // E-Rechnung abschaffen soll.
            //
            // Ausschliesslich der WERT, nie die Bezeichnung davor. BT-19 ist
            // ein maschinell gelesener Schluessel ("Buyer accounting
            // reference"); "PSP-Element: 4711-02" hineinzuschreiben ergaebe
            // einen Schluessel, den kein Buchungssystem des Empfaengers
            // wiederfindet. Eine Beschriftung dazu kennt EN 16931 nicht — sie
            // ist eine Frage des Andrucks und bleibt im PDF.
            //
            // Die Stelle ist vom UBL-Schema vorgegeben: cbc:AccountingCost
            // steht ZWISCHEN cbc:DocumentCurrencyCode und cbc:BuyerReference.
            // UBL ist eine strenge Sequenz — eine Zeile weiter unten waere kein
            // Schoenheitsfehler, sondern ein nicht schema-valides Dokument.
            leaf('cbc:AccountingCost', invoice?.details?.costCenter?.trim() || undefined),
            leaf('cbc:BuyerReference', resolveBuyerReference(invoice, customer)),
            hasPeriod
                ? group('cac:InvoicePeriod', [
                      leaf('cbc:StartDate', period.from),
                      leaf('cbc:EndDate', period.to),
                  ])
                : undefined,
            invoice?.details?.orderReference?.trim()
                ? group('cac:OrderReference', [leaf('cbc:ID', invoice.details.orderReference.trim())])
                : undefined,
            invoice?.details?.contractReference?.trim()
                ? group('cac:ContractDocumentReference', [
                      leaf('cbc:ID', invoice.details.contractReference.trim()),
                  ])
                : undefined,
            buildSellerParty(company, config),
            buildBuyerParty(customer),
            // Der Leistungszeitpunkt (BT-72) steht IMMER, wenn es einen gibt —
            // auch neben dem Abrechnungszeitraum (BT-73/74). EN 16931 laesst
            // beides nebeneinander zu, und die CII-Fassung sendet es ebenso;
            // die beiden beantworten verschiedene Fragen (wann geleistet wurde
            // gegenueber worueber abgerechnet wird).
            //
            // Frueher stand hier `!hasPeriod &&`: sobald ein Beleg einen
            // Abrechnungszeitraum trug — also bei praktisch jeder Rechnung —
            // fehlte BT-72 in der UBL-Fassung vollstaendig, waehrend die
            // CII-Fassung desselben Belegs ihn nannte. Der Empfaenger einer
            // XRechnung bekam damit keinen Leistungszeitpunkt.
            deliveryDate
                ? group('cac:Delivery', [leaf('cbc:ActualDeliveryDate', deliveryDate)])
                : undefined,
            group('cac:PaymentMeans', [
                leaf('cbc:PaymentMeansCode', resolvePaymentMeansCode(invoice)),
                leaf('cbc:PaymentID', invoice?.invoiceNumber),
                bank.iban
                    ? group('cac:PayeeFinancialAccount', [
                          leaf('cbc:ID', bank.iban),
                          leaf('cbc:Name', bank.holder),
                          group('cac:FinancialInstitutionBranch', [leaf('cbc:ID', bank.bic)]),
                      ])
                    : undefined,
            ]),
            paymentTerms ? group('cac:PaymentTerms', [leaf('cbc:Note', paymentTerms)]) : undefined,
            buildTaxTotal(totals?.taxGroups ?? [], totals?.taxTotalCents ?? 0),
            group('cac:LegalMonetaryTotal', [
                amount('cbc:LineExtensionAmount', totals?.netTotalCents ?? 0),
                amount('cbc:TaxExclusiveAmount', totals?.netTotalCents ?? 0),
                amount('cbc:TaxInclusiveAmount', totals?.grossTotalCents ?? 0),
                // BT-113 darf nur auftauchen, wenn wirklich etwas vereinnahmt
                // wurde; eine 0,00 provoziert bei manchen Pruefern eine Meldung.
                // "Vereinnahmt" heisst die tatsaechlich erhaltenen
                // Abschlagszahlungen, also Abschlaege abzueglich der dort
                // einbehaltenen Sicherheiten (en16931PrepaidCents) — und
                // BT-115 ist Brutto minus genau dieser Wert, also Zahlbetrag
                // PLUS kumuliertem Einbehalt. Der steht als Text in
                // BT-20/BT-22 (Begruendung dort).
                en16931PrepaidCents(totals) !== 0
                    ? amount('cbc:PrepaidAmount', en16931PrepaidCents(totals))
                    : undefined,
                amount('cbc:PayableAmount', en16931PayableCents(totals)),
            ]),
            ...lineNodes,
        ].filter((child): child is XmlNode => !!child),
    };

    return ['<?xml version="1.0" encoding="UTF-8"?>', ...renderNode(root, 0), ''].join('\n');
};
